/**
 * Shareable Selections — the pieces both sides need. The public projection is a
 * hand-written allowlist, never derived from the `Product` entity schema: a new
 * internal column must NOT reach a buyer by default.
 */
import { z } from 'zod';
import { Id } from './entities.js';
import { MoneyAmount, toScaledAmount } from './money.js';
/**
 * How a Selection shows money.
 *
 * `BAND` was cut on review and reinstated on 2026-08-04. The original hazard was
 * two band tables over two columns — MOQ banded `Product.listPrice`, display
 * banded `SelectionItem.unitPrice`, and a markup could shuffle an item into a
 * band that contradicted the price beside it. That is gone: `Product.moq` is a
 * stored column now, so `PRICE_BANDS` below is the only band left anywhere.
 */
export const PriceMode = z.enum(['NONE', 'EXACT', 'BAND']);
/**
 * Buyer-facing price bands, deliberately NOT the four in the one-off MOQ seed
 * script. Same-looking numbers serving two purposes is what made `BAND` a bug
 * the first time; these are wide enough to withhold the figure and coarse
 * enough to be worth reading.
 */
export const PRICE_BANDS = [
    { maxExclusive: '750.00', label: 'Under ₹750' },
    { maxExclusive: '1500.00', label: '₹750 – ₹1,500' },
    { maxExclusive: '2500.00', label: '₹1,500 – ₹2,500' },
    { maxExclusive: null, label: '₹2,500 and above' }
];
/**
 * The band an amount falls in. Exact `BigInt` comparison — this is the one place
 * in the feature that compares money without formatting it, and a float here
 * would put an item in the wrong band at a boundary.
 */
export function priceBandLabel(amount) {
    const scaled = toScaledAmount(amount);
    for (const band of PRICE_BANDS) {
        if (band.maxExclusive === null)
            return band.label;
        if (scaled < toScaledAmount(band.maxExclusive))
            return band.label;
    }
    const last = PRICE_BANDS[PRICE_BANDS.length - 1];
    if (last === undefined)
        throw new Error('PRICE_BANDS must not be empty');
    return last.label;
}
/**
 * What a buyer is allowed to see of one line's price, given the Selection's mode.
 *
 * The single decision point, so no route can render a band while also shipping
 * the figure it hides. Callers spread the result into the projection and must
 * not add `unitPrice` back alongside it.
 */
export function publicPrice(mode, unitPrice) {
    if (mode === 'EXACT' && unitPrice !== null)
        return { unitPrice };
    if (mode === 'BAND' && unitPrice !== null)
        return { priceBand: priceBandLabel(unitPrice) };
    return {};
}
/**
 * The buyer-visible reference on a Selection or Quotation — last 6 of the id,
 * upper-cased. Derived, never stored, and shared because both the public page
 * and the staff lead screen render it.
 */
export function selectionDocRef(id) {
    return id.slice(-6).toUpperCase();
}
/**
 * The staff-side create contract (Task 10, `POST /api/leads/:leadId/selections`).
 * Items are capped at `MAX_SELECTION_ITEMS` and written server-side via one
 * `createMany`, never a loop — see spec §API.
 */
export const CreateSelectionItemInput = z.object({
    productId: Id,
    /**
     * The catalogue price the builder marked up FROM, sent rather than re-read
     * server-side: a price edited between build and save would otherwise store a
     * `listPriceSnapshot` that does not match the basis of `unitPrice`, and the
     * margin shown later would be wrong.
     */
    listPriceSnapshot: MoneyAmount.nullable(),
    // Null under `NONE`. `EXACT`/`BAND` demand a price on every line — enforced
    // by the builder before submit, not by this schema, since the rule depends
    // on the sibling `priceMode` field.
    unitPrice: MoneyAmount.nullable(),
    sortOrder: z.number().int().nonnegative()
});
/**
 * The ceiling on one Selection, exported because the builder has to enforce it
 * BEFORE submit — "Add all" over an unfiltered catalogue is ~300 products, and
 * a schema rejection at save time would throw away the whole build.
 *
 * `CreateQuoteRequestInput`, `CreateQuotationInput` and `AcceptQuoteRequestInput`
 * deliberately take this same number rather than one of their own. A buyer raises
 * a request from a catalogue and we answer it with a quotation, so a lower cap
 * downstream is not a stricter rule — it is a dead end that only appears once
 * someone builds a full catalogue, on the buyer's public page or on the staff
 * accept screen. Raise it here and the whole chain moves together.
 */
export const MAX_SELECTION_ITEMS = 150;
export const CreateSelectionInput = z.object({
    leadId: Id,
    priceMode: PriceMode,
    expiresAt: z.iso.datetime().nullable().optional(),
    items: z.array(CreateSelectionItemInput).min(1).max(MAX_SELECTION_ITEMS)
});
/**
 * A Selection as the staff API returns it. Distinct from the buyer-facing
 * projection (a later task) — this one is never sent to `/api/share/:token`.
 */
export const SelectionItem = z.object({
    id: Id,
    productId: Id,
    productNameSnapshot: z.string().min(1),
    listPriceSnapshot: MoneyAmount.nullable(),
    unitPrice: MoneyAmount.nullable(),
    sortOrder: z.number().int().nonnegative()
});
export const Selection = z.object({
    id: Id,
    leadId: Id,
    priceMode: PriceMode,
    expiresAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    items: z.array(SelectionItem)
});
/**
 * The create response: the Selection plus its full share URL. Built
 * server-side from `env.BETTER_AUTH_URL` — never `window.location.origin`,
 * and never guessed client-side (spec §Web → Sending).
 */
export const CreatedSelection = Selection.extend({
    shareUrl: z.url()
});
/**
 * Editing a Selection in place, keeping its token — so a link already in a
 * buyer's hands shows the new set rather than dying. The item list REPLACES
 * the old one wholesale; there is no per-item patch, because the builder holds
 * the whole set in state anyway and a diff protocol would buy nothing.
 *
 * Safe to replace rows because `QuoteRequestItem` references `productId`, not
 * `selectionItemId` — a request already received keeps its meaning after an
 * edit. A revoked Selection is not editable (the API answers `CONFLICT`):
 * un-revoking through the back door would resurrect a link staff deliberately
 * killed.
 */
export const UpdateSelectionInput = z.object({
    priceMode: PriceMode,
    /** Unlike the items, this is a true patch: omitted leaves the expiry as it is, null clears it. */
    expiresAt: z.iso.datetime().nullable().optional(),
    items: z.array(CreateSelectionItemInput).min(1).max(MAX_SELECTION_ITEMS)
});
/**
 * What a buyer receives. **Hand-written, never derived from the `Product` entity
 * schema** — a deliberate exception to coding-standards' "don't duplicate a
 * schema, they drift", because here drift is the point: a new `Product` column
 * must stay private until somebody adds it here on purpose.
 *
 * Never carries a row id, `token`, `createdById`, `originalPath`,
 * `listPriceSnapshot`, or `isActive`. `itemNo` is the buyer-facing key — it is
 * what the shortlist and the quote request refer to.
 */
export const SharedSelectionItem = z
    .object({
    itemNo: z.number().int().positive(),
    name: z.string().min(1),
    description: z.string().nullable(),
    categoryName: z.string().nullable(),
    materialNames: z.array(z.string()),
    finishNames: z.array(z.string()),
    dimensionUnit: z.enum(['INCH', 'CM']),
    lengthCi: z.number().int().nullable(),
    widthCi: z.number().int().nullable(),
    heightCi: z.number().int().nullable(),
    diameterCi: z.number().int().nullable(),
    weightGrams: z.number().int().nullable(),
    moq: z.number().int().positive().nullable(),
    /**
     * `coverPath` keys only — the ~800px tier. The public media route pins that
     * tier itself, so a `thumbPath` or `originalPath` key here would 404 rather
     * than leak; listing only covers keeps the two ends agreeing.
     */
    coverKeys: z.array(z.string().min(1)),
    currency: z.string().min(1)
})
    /**
     * The price arrives by spreading `publicPrice()`, which returns exactly one of
     * these or neither. Declaring both optional is what makes it impossible to
     * ship a band and the figure it hides in the same object.
     */
    .extend({
    unitPrice: MoneyAmount.optional(),
    priceBand: z.string().optional()
});
export const SharedSelection = z.object({
    /** Derived from the id via `selectionDocRef()` — the id itself never travels. */
    docRef: z.string().min(1),
    priceMode: PriceMode,
    expiresAt: z.iso.datetime().nullable(),
    /**
     * Who this was prepared for, and who sent it. A Quotation has carried a
     * "Prepared for" masthead since it shipped; a Selection — the document most
     * buyers actually receive FIRST — was an anonymous product list under a
     * six-character code, with nobody named to reply to.
     *
     * Email and phone ride along too, to prefill the quote-request form — an
     * explicit call made 2026-08-05, reversing the narrower rule that used to
     * live here. The trade is real and was accepted knowingly: a Selection link
     * is expected to be forwarded (see `bumpViewCount` in routes/share.ts), so
     * these reach whoever it lands with next. Retyping an address on a phone was
     * judged the bigger loss, and name and company were already on the page.
     *
     * The line this does NOT cross: nothing else off the lead. No notes, no
     * stage, no prices paid, no visiting card. Add a field here only by making
     * the same call again, out loud.
     */
    recipientCompany: z.string().min(1),
    recipientName: z.string().nullable(),
    recipientEmail: z.string().nullable(),
    recipientPhone: z.string().nullable(),
    senderName: z.string().nullable(),
    items: z.array(SharedSelectionItem)
});
/**
 * A buyer's quote request (`POST /api/share/:token/quote-request`). Lines
 * refer to products by `itemNo` — the buyer never received a row id.
 */
export const CreateQuoteRequestItemInput = z.object({
    itemNo: z.number().int().positive(),
    quantity: z.number().int().positive(),
    /**
     * What the buyer wants changed on this piece — a finish, a size, their logo.
     * Capped well below a specification: this is a line on a request, and the
     * detail belongs in the conversation that follows.
     */
    note: z.string().trim().min(1).max(300).nullable().optional()
});
/**
 * A phone/WhatsApp number as a buyer types it. Deliberately permissive: buyers
 * are worldwide, so this asserts only "enough digits to dial" and keeps the
 * punctuation they typed — a per-country format check would reject real
 * numbers, and the office dials this by hand anyway.
 */
export const PhoneNumber = z
    .string()
    .trim()
    .regex(/^\+?[\d\s().-]+$/, 'Use digits, spaces and + ( ) - only.')
    .refine(value => {
    const digits = value.replace(/\D/g, '').length;
    return digits >= 7 && digits <= 15;
}, { message: 'Enter a reachable phone number, including the country code.' });
export const CreateQuoteRequestInput = z.object({
    requesterName: z.string().min(1),
    requesterCompany: z.string().min(1),
    requesterEmail: z.email(),
    /**
     * Required since 2026-08-10. Email alone left the office unable to chase a
     * request; WhatsApp is how these conversations actually continue. Existing
     * rows and the staff-facing summary stay nullable — this is a rule for new
     * requests, not a rewrite of history.
     */
    requesterPhone: PhoneNumber,
    message: z.string().min(1).optional(),
    items: z.array(CreateQuoteRequestItemInput).min(1).max(MAX_SELECTION_ITEMS)
});
export const CreatedQuoteRequest = z.object({
    id: Id
});
/**
 * A quote-request line as the staff lead screen shows it — resolved through
 * `QuoteRequestItem.product`, not the buyer-supplied `itemNo`, since the
 * request already reached a specific product row server-side.
 */
export const QuoteRequestSummaryItem = z.object({
    id: Id,
    productId: Id,
    productName: z.string().min(1),
    itemNo: z.number().int().positive(),
    quantity: z.number().int().positive(),
    /** The buyer's customization request for this line, verbatim. Null when they asked for none. */
    note: z.string().nullable(),
    /**
     * What the parent Selection showed for this product, so the price-and-accept
     * screen prefills instead of asking twice — a Selection sent at `EXACT` is
     * then one confirming tap. Null when it was sent at `NONE`, which is the
     * default and exactly why accept has to be a screen at all.
     */
    selectionUnitPrice: MoneyAmount.nullable(),
    /** Carried straight across to the Quotation — never re-read from the catalogue. */
    selectionListPriceSnapshot: MoneyAmount.nullable()
});
/** A buyer's quote request as it surfaces on the lead detail screen. */
/** `PENDING` until someone answers it; both other states are reachable only through an endpoint a human triggers. */
export const QuoteRequestStatus = z.enum(['PENDING', 'ACCEPTED', 'DECLINED']);
export const QuoteRequestSummary = z.object({
    id: Id,
    /**
     * Whether anyone has answered yet. The lead screen hides "Price & respond"
     * once this leaves PENDING — the API refuses a second answer anyway, and a
     * live button that always errors is worse than no button.
     */
    status: QuoteRequestStatus,
    requesterName: z.string().min(1),
    requesterCompany: z.string().min(1),
    requesterEmail: z.email(),
    requesterPhone: z.string().nullable(),
    message: z.string().nullable(),
    createdAt: z.iso.datetime(),
    items: z.array(QuoteRequestSummaryItem)
});
// ---------------------------------------------------------------------------
// Quotations — the priced document sent back. Distinct from a Selection in one
// way that drives every schema below: `QuotationItem.unitPrice` is NOT NULL.
// A quotation with a blank price is not a quotation, which is what makes
// `accept` a price-and-accept step rather than a one-click copy of a Selection
// whose `priceMode` was NONE.
// ---------------------------------------------------------------------------
export const QuotationStatus = z.enum(['SENT', 'ACCEPTED', 'DECLINED']);
/**
 * The commercial small print under a priced document. Stored per Quotation
 * rather than derived at render, so a quotation sent last month still prints
 * the terms it was sent under when the rate or the packing basis changes.
 *
 * This is the DEFAULT staff see prefilled, not a constant the document is
 * bound to — the accept screen offers it and they edit or clear it per buyer.
 */
export const DEFAULT_QUOTATION_TERMS = 'GST @ 18% extra. Prices are ex-factory in standard packaging.';
/** Guards a footnote from becoming a second description. Empty means "no terms". */
export const QuotationTerms = z.string().max(600).nullable().optional();
export const CreateQuotationItemInput = z.object({
    productId: Id,
    /**
     * Carried across from what the buyer was already shown — never re-read from
     * the catalogue. The price they saw is the price that must reach the
     * document, and a catalogue edit in between must not rewrite it.
     */
    listPriceSnapshot: MoneyAmount.nullable(),
    /** Required, unlike a Selection line. This is the field `accept` exists to collect. */
    unitPrice: MoneyAmount,
    quantity: z.number().int().positive(),
    note: z.string().min(1).nullable().optional(),
    sortOrder: z.number().int().nonnegative()
});
const recipientFields = {
    recipientName: z.string().min(1),
    recipientCompany: z.string().min(1),
    recipientEmail: z.email().nullable().optional(),
    recipientPhone: z.string().min(1).nullable().optional()
};
/**
 * Raising a Quotation directly (`POST /api/leads/:leadId/quotations`) — the
 * WhatsApp path, and the majority one. The recipient is supplied here because
 * there is no `QuoteRequest` to snapshot it from.
 */
export const CreateQuotationInput = z.object({
    ...recipientFields,
    expiresAt: z.iso.datetime().nullable().optional(),
    terms: QuotationTerms,
    items: z.array(CreateQuotationItemInput).min(1).max(MAX_SELECTION_ITEMS)
});
/**
 * Answering a request (`POST /api/quote-requests/:id/accept`). No recipient
 * fields: they are snapshotted server-side from the `QuoteRequest`, so the
 * person who asked is the person the document names.
 */
export const AcceptQuoteRequestInput = z.object({
    expiresAt: z.iso.datetime().nullable().optional(),
    terms: QuotationTerms,
    items: z.array(CreateQuotationItemInput).min(1).max(MAX_SELECTION_ITEMS)
});
export const UpdateQuotationStatusInput = z.object({
    status: z.enum(['ACCEPTED', 'DECLINED'])
});
export const QuotationItem = z.object({
    id: Id,
    productId: Id,
    productNameSnapshot: z.string().min(1),
    listPriceSnapshot: MoneyAmount.nullable(),
    unitPrice: MoneyAmount,
    quantity: z.number().int().positive(),
    note: z.string().nullable().optional(),
    sortOrder: z.number().int().nonnegative()
});
/** A Quotation as the staff API returns it. Never sent to `/api/q/:token`. */
export const Quotation = z.object({
    id: Id,
    leadId: Id,
    quoteRequestId: Id.nullable(),
    ...recipientFields,
    recipientEmail: z.email().nullable(),
    recipientPhone: z.string().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    status: QuotationStatus,
    supersedesId: Id.nullable(),
    /** Null means this row IS the root. Group on `rootQuotationId ?? id`. */
    rootQuotationId: Id.nullable(),
    revision: z.number().int().positive(),
    terms: z.string().nullable(),
    createdAt: z.iso.datetime(),
    items: z.array(QuotationItem)
});
export const CreatedQuotation = Quotation.extend({
    shareUrl: z.url()
});
/**
 * What a buyer receives at `/q/:token`. Hand-written for the same reason as
 * `SharedSelectionItem`: a new internal column must stay private until somebody
 * publishes it deliberately. No `listPriceSnapshot` — what the office paid or
 * listed is not the buyer's business — and no row ids, token or status.
 *
 * Unlike a Selection there is no `publicPrice()` indirection: a Quotation has
 * no price mode, because a document without prices is not a quotation.
 */
export const SharedQuotationItem = z.object({
    itemNo: z.number().int().positive(),
    name: z.string().min(1),
    description: z.string().nullable(),
    dimensionUnit: z.enum(['INCH', 'CM']),
    lengthCi: z.number().int().nullable(),
    widthCi: z.number().int().nullable(),
    heightCi: z.number().int().nullable(),
    diameterCi: z.number().int().nullable(),
    weightGrams: z.number().int().nullable(),
    coverKeys: z.array(z.string().min(1)),
    unitPrice: MoneyAmount,
    quantity: z.number().int().positive(),
    lineTotal: MoneyAmount,
    note: z.string().nullable(),
    currency: z.string().min(1)
});
export const SharedQuotation = z.object({
    docRef: z.string().min(1),
    revision: z.number().int().positive(),
    recipientName: z.string().min(1),
    recipientCompany: z.string().min(1),
    /**
     * Who raised it. The page needs a person to point the buyer at — the app has
     * no buyer-facing Accept, so "reply to whoever sent you this" is the actual
     * next step and it has to name somebody. A name only: no staff email or
     * phone goes on a public page.
     */
    senderName: z.string().nullable(),
    /** The small print, as it was when this was sent. Null prints no footnote at all. */
    terms: z.string().nullable(),
    /**
     * Whether the buyer has already accepted, so a reopened link shows the
     * outcome instead of offering the decision a second time.
     */
    status: QuotationStatus,
    /**
     * The office WhatsApp number, digits only, for the "Ask for a change" button.
     * Null when the deployment has not configured one — the page then falls back
     * to "reply on the message that brought you this link".
     */
    contactWhatsapp: z.string().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    items: z.array(SharedQuotationItem),
    /** Summed server-side with `sumLineTotals` so the page never does money arithmetic. */
    total: MoneyAmount,
    currency: z.string().min(1)
});
// ---------------------------------------------------------------------------
// Lead-screen summaries (S4). These go to AUTHENTICATED staff, so unlike the
// `Shared*` allowlists above they may carry ids and the share URL — staff have
// to be able to copy the link they sent.
// ---------------------------------------------------------------------------
export const SelectionSummary = z.object({
    id: Id,
    docRef: z.string().min(1),
    priceMode: PriceMode,
    itemCount: z.number().int().nonnegative(),
    /** Per LINK, not per person — there are no visitor rows, by design (spec §B, the gate was cut). */
    viewCount: z.number().int().nonnegative(),
    firstViewedAt: z.iso.datetime().nullable(),
    lastViewedAt: z.iso.datetime().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    shareUrl: z.url()
});
export const QuotationSummary = z.object({
    id: Id,
    docRef: z.string().min(1),
    /**
     * Revisions are grouped on `rootQuotationId ?? id`. That column exists so the
     * lead screen can collapse a chain with one pass; walking `supersedesId`
     * instead is a recursive CTE server-side or an O(n²) link-up client-side.
     */
    rootQuotationId: Id.nullable(),
    supersedesId: Id.nullable(),
    revision: z.number().int().positive(),
    status: QuotationStatus,
    recipientName: z.string().min(1),
    recipientCompany: z.string().min(1),
    itemCount: z.number().int().nonnegative(),
    total: MoneyAmount,
    viewCount: z.number().int().nonnegative(),
    firstViewedAt: z.iso.datetime().nullable(),
    lastViewedAt: z.iso.datetime().nullable(),
    expiresAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    shareUrl: z.url()
});
/**
 * A pending quote request as the board surfaces it (S7). The board's whole job
 * here is "somebody is waiting" — enough to identify the buyer and get to them,
 * nothing more.
 */
export const PendingQuoteRequest = z.object({
    id: Id,
    leadId: Id,
    leadCompany: z.string().min(1),
    requesterName: z.string().min(1),
    requesterCompany: z.string().min(1),
    itemCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime()
});
//# sourceMappingURL=selections.js.map